import { getLocalization } from 'global/global';
import { useAppDispatch, useAppSelector } from 'index';
import { NavItemRef } from 'Interfaces/Admin';
import { showAlert, showConfirm } from 'actions';
import { saveAdminGroup } from 'actions/adminGroupActions';
import { saveAdminUser } from 'actions/adminUserActions';
import { adminGroupsSelector, adminUsersSelector } from 'reducers/admin/selectors';
import { canReparent, isNoOpMove } from './reparentRules';

/**
 *  Confirms and performs a drag-and-drop reparent, replacing revertDragged from
 *  admin.js. Returns a handler for the drop event.
 */
export const useReparent = () => {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(adminGroupsSelector);
  const users = useAppSelector(adminUsersSelector);
  const currentGroupId = useAppSelector((state) => state.admin.account.groupId);

  return (source: NavItemRef, targetGroupId: number): void => {
    if (isNoOpMove(groups, users, source, targetGroupId)) {
      return;
    }

    const check = canReparent(groups, users, source, targetGroupId);
    if (!check.allowed) {
      dispatch(showAlert({
        visible: true,
        message: `${getLocalization('maxusersreached')} ${check.blockedBy || ''}`.trim(),
      }));
      return;
    }

    const target = groups.find((g) => g.id === targetGroupId);
    if (!target) {
      return;
    }

    const message = source.type === 'user'
      ? getLocalization('confirmUserMove')
        .replace('%user%', users.find((u) => u.id === source.id)?.userName || '')
        .replace('%group%', target.name)
      : getLocalization('confirmGroupMove')
        .replace('%source%', groups.find((g) => g.id === source.id)?.name || '')
        .replace('%target%', target.name);

    dispatch(showConfirm({
      visible: true,
      message,
      onConfirm: () => {
        if (source.type === 'user') {
          const user = users.find((u) => u.id === source.id);
          if (user) {
            void dispatch(saveAdminUser({ ...user, groupId: targetGroupId }));
          }
        } else {
          const group = groups.find((g) => g.id === source.id);
          if (group) {
            void dispatch(saveAdminGroup(
              { ...group, parentId: targetGroupId, added_by: currentGroupId },
              group.active,
            ));
          }
        }
      },
    }));
  };
};
